import { Day, Locale } from 'date-fns';
import { de, enGB, enUS } from 'date-fns/locale';
import { setDefaultOptions } from 'date-fns/setDefaultOptions';

/**
 * Identifiers of the locales the application can format dates in.
 *
 * These intentionally mirror the IDs the previous date library used so persisted user settings keep
 * resolving to the same formats.
 */
export type DateLocaleId = 'de' | 'en' | 'en-gb';

/**
 * The localized format tokens.
 *
 * `L` is the numeric date, `LL` the long date, `ll` the long date with an abbreviated month, `LT`
 * the time and `LTS` the time including seconds.
 * date-fns ships equivalents (`P`, `PPP`, `p`, `pp`), but its English long date renders an ordinal
 * day ("April 29th, 1453"). Spelling the patterns out keeps the rendered output unchanged.
 */
export type LongDateFormatToken = 'L' | 'LL' | 'll' | 'LT' | 'LTS';

interface DateLocaleConfig {
  formats: Record<LongDateFormatToken, string>;
  locale: Locale;
}

const CONFIGS: Record<DateLocaleId, DateLocaleConfig> = {
  de: {
    formats: {
      L: 'dd.MM.yyyy',
      LL: 'd. MMMM yyyy',
      ll: 'd. MMM yyyy',
      LT: 'HH:mm',
      LTS: 'HH:mm:ss',
    },
    locale: de,
  },
  en: {
    formats: {
      L: 'MM/dd/yyyy',
      LL: 'MMMM d, yyyy',
      ll: 'MMM d, yyyy',
      LT: 'h:mm a',
      LTS: 'h:mm:ss a',
    },
    locale: enUS,
  },
  'en-gb': {
    formats: { L: 'dd/MM/yyyy', LL: 'd MMMM yyyy', ll: 'd MMM yyyy', LT: 'HH:mm', LTS: 'HH:mm:ss' },
    locale: enGB,
  },
};

let currentLocaleId: DateLocaleId = 'en';

/**
 * Sets the locale every date-fns call defaults to.
 *
 * date-fns is functional and takes the locale per call, so this writes it into date-fns' default
 * options instead of onto a wrapper object.
 *
 * @param localeId - The locale to activate.
 */
export function setDateLocale(localeId: DateLocaleId): void {
  currentLocaleId = localeId;
  setDefaultOptions({ locale: CONFIGS[localeId].locale });
}

/**
 * Returns the currently active locale ID.
 */
export function getDateLocale(): DateLocaleId {
  return currentLocaleId;
}

/**
 * Returns the date-fns locale object for the currently active locale.
 *
 * Only needed where a locale has to be passed explicitly; plain `format` calls already pick it up
 * from the default options.
 */
export function getDateFnsLocale(): Locale {
  return CONFIGS[currentLocaleId].locale;
}

/**
 * Resolves a localized format token to a date-fns format pattern for the active locale.
 *
 * @param token - The localized format token.
 * @returns The format pattern, e.g. `dd.MM.yyyy` for `L` in German.
 */
export function getLongDateFormat(token: LongDateFormatToken): string {
  return CONFIGS[currentLocaleId].formats[token];
}

/**
 * Returns the weekday names of the active locale, starting at Monday.
 *
 * @param width - The width of the names. `short` yields the shortest form ("Mo"), `abbreviated` the
 * abbreviated form ("Mon").
 * @returns Seven weekday names, Monday first.
 */
export function getWeekdayNames(width: 'abbreviated' | 'short'): string[] {
  const { localize } = getDateFnsLocale();

  // `localize.day` indexes Sunday as 0, so start at Monday and wrap Sunday around to the end.
  return Array.from({ length: 7 }, (_, index) => localize.day(((index + 1) % 7) as Day, { width }));
}
